<template>
  <div class="live-chat-json">
    <div class="live-chat-json__panel panzoom-ignore">
      <v-btn outlined color="primary" class="mb-3" @click="triggerJsonUpload">
        <v-icon small class="mr-1">mdi-code-json</v-icon>
        Upload JSON file
      </v-btn>
      <input
        ref="jsonInput"
        type="file"
        class="d-none"
        accept=".json,application/json"
        @change="onJsonFileSelected" />

      <div class="live-chat-json__title">Media files</div>
      <p class="live-chat-json__hint">
        Every media file referenced in the JSON (audio, avatar, and images/gifs in content) is grouped by name. Click a
        group to see each location. Upload a new file or rename it — for the whole group or a single location — and jump
        to it in the JSON.
      </p>

      <div class="live-chat-json__scroll">
        <template v-for="section in mediaSections">
          <div v-if="section.groups.length" :key="section.title" class="live-chat-json__section-title">
            {{ section.title }}
          </div>
          <div v-for="group in section.groups" :key="group.name" class="live-chat-json__group">
            <div class="live-chat-json__row">
              <template v-if="editingKey === group.name">
                <v-text-field
                  v-model="editingValue"
                  class="live-chat-json__edit-field"
                  dense
                  hide-details
                  autofocus
                  @keyup.enter="saveEdit({ type: 'group', name: group.name })"
                  @keyup.esc="cancelEdit"></v-text-field>
                <v-btn icon x-small color="primary" @click="saveEdit({ type: 'group', name: group.name })">
                  <v-icon small>mdi-check</v-icon>
                </v-btn>
                <v-btn icon x-small @click="cancelEdit"><v-icon small>mdi-close</v-icon></v-btn>
              </template>

              <template v-else>
                <v-icon small class="mr-1" @click="toggleGroup(group.name)">
                  {{ expanded[group.name] ? "mdi-chevron-down" : "mdi-chevron-right" }}
                </v-icon>
                <span class="live-chat-json__file-name" @click="toggleGroup(group.name)">{{ group.name }}</span>
                <span class="live-chat-json__count">×{{ group.count }}</span>
                <v-spacer></v-spacer>
                <v-btn
                  icon
                  x-small
                  title="Upload a new file for every location"
                  :loading="uploadingKey === group.name"
                  @click="triggerUpload({ type: 'group', name: group.name })">
                  <v-icon small>mdi-upload</v-icon>
                </v-btn>
                <v-btn icon x-small title="Rename in every location" @click="startEdit(group.name, group.name)">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
            </div>

            <div v-if="expanded[group.name]" class="live-chat-json__locations">
              <div v-for="location in group.locations" :key="locationKey(location)" class="live-chat-json__row">
                <template v-if="editingKey === locationKey(location)">
                  <v-text-field
                    v-model="editingValue"
                    class="live-chat-json__edit-field"
                    dense
                    hide-details
                    autofocus
                    @keyup.enter="saveEdit({ type: 'location', location })"
                    @keyup.esc="cancelEdit"></v-text-field>
                  <v-btn icon x-small color="primary" @click="saveEdit({ type: 'location', location })">
                    <v-icon small>mdi-check</v-icon>
                  </v-btn>
                  <v-btn icon x-small @click="cancelEdit"><v-icon small>mdi-close</v-icon></v-btn>
                </template>

                <template v-else>
                  <span
                    class="live-chat-json__file-name live-chat-json__file-name--clickable"
                    @click="scrollToLocation(location)">
                    {{ locationLabel(location) }}<template v-if="location.line"> · line {{ location.line }}</template>
                  </span>
                  <v-spacer></v-spacer>
                  <v-btn
                    icon
                    x-small
                    title="Upload a new file for this location"
                    :loading="uploadingKey === locationKey(location)"
                    @click="triggerUpload({ type: 'location', location })">
                    <v-icon small>mdi-upload</v-icon>
                  </v-btn>
                  <v-btn icon x-small title="Rename this location" @click="startEdit(locationKey(location), group.name)">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
              </div>
            </div>
          </div>
        </template>

        <div v-if="!mediaGroups.length" class="live-chat-json__hint">No media file references found in the JSON.</div>
      </div>

      <input
        ref="uploadInput"
        type="file"
        class="d-none"
        accept=".mp3,.wav,.m4a,.ogg,.mp4,.webm,.gif,.png,.jpg,.jpeg,.webp,.svg"
        @change="onUploadFileSelected" />
    </div>

    <div class="live-chat-json__editor">
      <div v-if="jsonFlowError" class="d-flex align-center mb-3">
        <span class="json-preview__format-error">{{ jsonFlowError }}</span>
      </div>
      <div v-if="jsonFormatError" class="d-flex align-center mb-3">
        <span class="json-preview__format-error">Invalid JSON format!</span>
        <v-tooltip top>
          <template #activator="{ on }">
            <v-btn v-on="on" @click="undoJson" class="ml-3">
              <v-icon>mdi-undo</v-icon>
            </v-btn>
          </template>
          <p>Undo to previous valid JSON state</p>
        </v-tooltip>
      </div>
      <code-input
        :class="{ 'code-error': jsonFormatError }"
        :key="jsonInputKey"
        v-model="liveChatDataJsonFormat"
        mode="application/json"
        :title="''"
        :options="codeMirrorOptions"
        @ready="onEditorReady"></code-input>
    </div>
  </div>
</template>

<script>
  import CodeInput from "@/components/custom/CodeInput";
  import debounce from "lodash.debounce";
  import { sendToServer } from "@/utility/mediaObjectUploadHelper";
  import { toAbsoluteAssetUrl } from "@/utility/convertAssetsPaths";
  import { formErrorMessage } from "@/utility/formErrorMessage";
  import { flowErrorMessage, normalizeIds } from "./treeGraph";

  const DEFAULT_COUNTER_BOTTOM = {
    enabled: false,
    minutes: "",
    seconds: "",
    title: "",
    subtitle: "",
  };

  const CONTENT_MEDIA_REGEX = /(?:src|href)=["']([^"']+\.(?:gif|png|jpe?g|webp|svg|mp4|webm|mp3|wav|m4a|ogg))["']/gi;

  export default {
    name: "LiveChatJsonMode",
    components: { CodeInput },
    props: {
      // The live chat data object, mutated in place (same pattern as LiveChatTree's message-tree).
      liveChatData: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        jsonFormatError: false,
        jsonFlowError: null,
        jsonInputKey: 0,
        // Finite viewportMargin so CodeMirror virtualizes (renders only visible lines) — keeps big JSON fast.
        codeMirrorOptions: { viewportMargin: 10 },
        uploadingKey: null,
        uploadTarget: null,
        editingKey: null,
        editingValue: "",
        expanded: {},
        cmEditor: null,
        mediaGroups: [],
      };
    },
    computed: {
      liveChatDataJsonFormat: {
        get() {
          return JSON.stringify(this.liveChatData, null, " ");
        },
        set(value) {
          let parsedJson;
          try {
            parsedJson = JSON.parse(value);
          } catch (e) {
            this.jsonFormatError = true;
            return;
          }
          if (!parsedJson || typeof parsedJson !== "object" || Array.isArray(parsedJson)) {
            this.jsonFormatError = true;
            return;
          }
          this.jsonFormatError = false;

          // Skip editorMode (editor UI state) — a JSON edit/upload must not switch the mode.
          Object.keys(parsedJson).forEach(key => {
            if (key === "editorMode") return;
            this.$set(this.liveChatData, key, parsedJson[key]);
          });
          // Never delete a schema key present at open (would crash tree mode); only user-added keys.
          Object.keys(this.liveChatData).forEach(key => {
            if (!this.protectedKeys.has(key) && !(key in parsedJson)) {
              this.$delete(this.liveChatData, key);
            }
          });
          this.$set(this.liveChatData, "counterBottom", { ...DEFAULT_COUNTER_BOTTOM, ...parsedJson.counterBottom });
          // Validate the committed data, not the raw text — protected keys survive being deleted from it.
          this.jsonFlowError = flowErrorMessage(this.liveChatData.messages);
        },
      },
      mediaSections() {
        return [
          { title: "Images & GIFs", groups: this.mediaGroups.filter(group => group.type === "image") },
          { title: "Audio & Video", groups: this.mediaGroups.filter(group => group.type === "av") },
        ];
      },
    },
    watch: {
      liveChatData: {
        handler() {
          this.debouncedComputeGroups();
        },
        deep: true,
      },
    },
    created() {
      this.protectedKeys = new Set(Object.keys(this.liveChatData));
      // Debounce the O(n) media scan so typing in a big tree stays smooth.
      this.debouncedComputeGroups = debounce(() => {
        this.mediaGroups = this.computeMediaGroups();
      }, 300);
      this.mediaGroups = this.computeMediaGroups();
      this.jsonFlowError = flowErrorMessage(this.liveChatData.messages);
    },
    beforeDestroy() {
      this.debouncedComputeGroups.cancel();
    },
    methods: {
      onEditorReady(editor) {
        this.cmEditor = editor;
      },
      triggerJsonUpload() {
        this.$refs.jsonInput.value = "";
        this.$refs.jsonInput.click();
      },
      async onJsonFileSelected(event) {
        const file = event.target.files && event.target.files[0];
        if (!file) return;

        const textContent = await file.text();
        // Reuse the editor's setter so the uploaded JSON is parsed and copied into the content.
        this.liveChatDataJsonFormat = textContent;
        if (!this.jsonFormatError) {
          // Uploaded files routinely lack ids; backfill before the tree sees them.
          normalizeIds(this.liveChatData.messages);
          this.jsonFlowError = flowErrorMessage(this.liveChatData.messages);
        }
        this.jsonInputKey++;
        if (this.jsonFormatError) {
          this.$admin.toast.error("Invalid JSON file");
        }
      },
      toggleGroup(name) {
        this.$set(this.expanded, name, !this.expanded[name]);
      },
      baseName(url) {
        return url.split("/").pop().split("?")[0];
      },
      // Swap only the basename (keep dir + ?query/#hash); a value with a "/" or scheme replaces the whole URL.
      renameUrl(url, newName) {
        if (newName.includes("/") || /^[a-z][a-z\d+.-]*:/i.test(newName)) return newName;
        if (typeof url !== "string" || !url) return newName;
        const lastSlash = url.lastIndexOf("/");
        const dir = lastSlash >= 0 ? url.slice(0, lastSlash + 1) : "";
        const filename = lastSlash >= 0 ? url.slice(lastSlash + 1) : url;
        const suffix = (filename.match(/[?#].*$/) || [""])[0];
        return dir + newName + suffix;
      },
      mediaType(name) {
        const extension = (name.split(".").pop() || "").toLowerCase();

        return ["gif", "png", "jpg", "jpeg", "webp", "svg"].includes(extension) ? "image" : "av";
      },
      locationKey(location) {
        return `${location.kind}:${location.path.join(".")}:${location.src || ""}`;
      },
      locationLabel(location) {
        const labels = { audio: "Audio", imageObject: "Avatar", contentImage: "Image" };
        return labels[location.kind] || "Location";
      },
      // Resolve each `line` from its `anchor` in document order so repeated basenames stay accurate.
      computeMediaGroups() {
        const text = JSON.stringify(this.liveChatData, null, " ");
        const groups = new Map();
        this.collectMediaRefs().forEach(ref => {
          if (false === groups.has(ref.name)) {
            groups.set(ref.name, []);
          }
          groups.get(ref.name).push(ref);
        });

        const cursors = new Map();
        const lineOf = anchor => {
          const from = cursors.get(anchor) || 0;
          const index = text.indexOf(anchor, from);
          if (index === -1) return null;
          cursors.set(anchor, index + anchor.length);

          return text.slice(0, index).split("\n").length;
        };

        return [...groups.entries()].map(([name, refs]) => ({
          name,
          type: this.mediaType(name),
          count: refs.length,
          locations: refs.map(ref => ({ ...ref, line: lineOf(ref.anchor) })),
        }));
      },
      collectMediaRefs() {
        const refs = [];
        const avatar = this.liveChatData.botAvatar;
        if (avatar && typeof avatar.imageURL === "string" && avatar.imageURL.trim()) {
          refs.push({
            kind: "imageObject",
            name: this.baseName(avatar.imageURL),
            path: ["botAvatar"],
            src: avatar.imageURL,
            anchor: avatar.imageURL,
          });
        }

        const walk = (node, path) => {
          if (Array.isArray(node)) {
            node.forEach((child, index) => walk(child, [...path, index]));
            return;
          }
          if (!node || typeof node !== "object") return;

          if (typeof node.fileName === "string" && node.fileName.trim()) {
            const name = node.fileName.trim();
            // Anchor on the quoted value so "one.mp3" doesn't match inside "gone.mp3".
            refs.push({ kind: "audio", name, path, anchor: `"${name}"` });
          }

          ["content", "text", "value"].forEach(field => {
            if (typeof node[field] === "string") {
              const matches = node[field].matchAll(CONTENT_MEDIA_REGEX);
              for (const match of matches) {
                refs.push({
                  kind: "contentImage",
                  name: this.baseName(match[1]),
                  path: [...path, field],
                  src: match[1],
                  anchor: match[1],
                });
              }
            }
          });

          Object.keys(node).forEach(key => walk(node[key], [...path, key]));
        };
        walk(this.liveChatData, []);

        return refs;
      },
      resolveObject(path) {
        let object = this.liveChatData;
        for (const key of path) {
          object = object[key];
        }

        return object;
      },
      setFields(path, fields) {
        const object = this.resolveObject(path);
        Object.keys(fields).forEach(field => this.$set(object, field, fields[field]));
      },
      // Rewrites the element, not the raw string: TinyMCE gives an <img> a `srcset` of the old
      // file's responsive variants, and a candidate from it outranks the new `src` in the browser.
      replaceInField(path, oldValue, newValue) {
        const parent = this.resolveObject(path.slice(0, -1));
        const key = path[path.length - 1];
        if (typeof parent[key] !== "string") return;

        const doc = new DOMParser().parseFromString(parent[key], "text/html");
        let changed = false;

        doc.querySelectorAll("[src],[href],[poster]").forEach(element => {
          ["src", "href", "poster"].forEach(attribute => {
            if (element.getAttribute(attribute) !== oldValue) return;

            element.setAttribute(attribute, newValue);
            if (element.hasAttribute(`data-mce-${attribute}`)) {
              element.setAttribute(`data-mce-${attribute}`, newValue);
            }
            // The variants belong to the replaced file; the panel uploads without generating new ones.
            if (attribute === "src") {
              element.removeAttribute("srcset");
              element.removeAttribute("data-mce-srcset");
            }
            changed = true;
          });
        });

        if (changed) this.$set(parent, key, doc.body.innerHTML);
      },
      applyToLocation(location, { url, id, rename }) {
        if (location.kind === "audio") {
          // Upload keeps the existing fileName; only fileURL/fileID point to the new file.
          this.setFields(location.path, rename ? { fileName: rename } : { fileID: id, fileURL: url });
        } else if (location.kind === "imageObject") {
          this.setFields(
            location.path,
            rename ? { imageURL: this.renameUrl(location.src, rename) } : { imageID: id, imageURL: url },
          );
        } else {
          const replacement = rename ? this.renameUrl(location.src, rename) : toAbsoluteAssetUrl(url);
          this.replaceInField(location.path, location.src, replacement);
        }
      },
      writeToTarget(target, payload) {
        if (target.type === "group") {
          this.collectMediaRefs()
            .filter(ref => ref.name === target.name)
            .forEach(ref => this.applyToLocation(ref, payload));
        } else {
          this.applyToLocation(target.location, payload);
        }
        this.jsonInputKey++;
      },
      targetKey(target) {
        return target.type === "group" ? target.name : this.locationKey(target.location);
      },
      triggerUpload(target) {
        this.uploadTarget = target;
        this.$refs.uploadInput.value = "";
        this.$refs.uploadInput.click();
      },
      async onUploadFileSelected(event) {
        const file = event.target.files && event.target.files[0];
        if (!file || file.size === 0 || !this.uploadTarget) return;

        const target = this.uploadTarget;
        this.uploadingKey = this.targetKey(target);
        try {
          const res = await sendToServer(file);
          this.writeToTarget(target, { url: res.contentUrl, id: res["@id"] });
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.uploadingKey = null;
          this.uploadTarget = null;
        }
      },
      startEdit(key, value) {
        this.editingKey = key;
        this.editingValue = value;
      },
      cancelEdit() {
        this.editingKey = null;
        this.editingValue = "";
      },
      saveEdit(target) {
        const value = this.editingValue.trim();
        if (value) {
          this.writeToTarget(target, { rename: value });
        }
        this.cancelEdit();
      },
      undoJson() {
        this.liveChatDataJsonFormat = JSON.stringify(this.liveChatData, null, " ");
        this.jsonFormatError = false;
        this.jsonInputKey++;
      },
      scrollToLocation(location) {
        if (!this.cmEditor || !location.line) return;

        const line = location.line - 1; // location.line is 1-based
        this.cmEditor.focus();
        this.cmEditor.setCursor({ line, ch: 0 });
        this.cmEditor.scrollIntoView({ line, ch: 0 }, 200);
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/typography/index.scss";
  @import "@core/preset/preset/functions.scss";

  .live-chat-json {
    display: flex;
    gap: 16px;
    padding: 16px;
    height: 100vh;
    overflow: hidden;
    box-sizing: border-box;

    &__panel {
      flex: 0 0 360px;
      max-width: 360px;
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      padding: 16px;
      background: get-color("element-bg-100");
      border: 1px solid get-color("border");
      border-radius: 6px;
    }

    &__scroll {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      // Keep the scrollbar clear of the right-aligned action buttons
      padding-right: 10px;
    }

    &__editor {
      flex: 1 1 auto;
      min-width: 0;
      min-height: 0;
      display: flex;
      flex-direction: column;

      // Fixed-height CodeMirror with its own scroll so it virtualizes large documents
      &::v-deep .va-code-input,
      &::v-deep .vue-codemirror {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
      }

      &::v-deep .CodeMirror {
        flex: 1 1 auto;
        min-height: 0;
        height: 100%;
      }
    }

    &__title {
      @include heading-4();
      color: get-color("headline");
      margin-bottom: 8px;
    }

    &__hint {
      @include text-caption();
      color: get-color("subtitle");
      margin-bottom: 16px;
    }

    &__section-title {
      @include text-body-2-bold();
      color: get-color("headline");
      margin: 10px 0 4px;
    }

    &__row {
      display: flex;
      align-items: center;
      padding: 2px 0;
      @include text-body-2();
      color: get-color("body");
    }

    &__locations {
      padding-left: 22px;
    }

    &__file-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      cursor: pointer;

      &--clickable:hover {
        text-decoration: underline;
      }
    }

    &__count {
      @include text-caption();
      color: get-color("subtitle");
      margin-left: 6px;
    }
  }

  .json-preview__format-error {
    color: get-color("error");
    @include text-body-1-bold();
  }
  .code-error {
    border: 3px dashed get-color("error") !important;
  }
</style>
